import React, { useState, useCallback, type ReactNode } from 'react';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Popup from '@atlaskit/popup';
import { Stack, Text, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { layers } from '@atlassian/jira-common-styles/src';

const ICON_COLOR = token('color.icon.discovery', colors.P300);

type Props = {
	heading?: string | ReactNode;
	description: string | ReactNode;
};

export const InfoButton = ({ heading, description }: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const onClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	const toggleIsOpen = useCallback(() => {
		setIsOpen(!isOpen);
	}, [isOpen]);

	const getContent = useCallback(
		() => (
			<Stack xcss={contentWrapperStyle} space="space.100">
				<Heading size="xsmall">{heading}</Heading>
				<Text>{description}</Text>
			</Stack>
		),
		[description, heading],
	);

	return (
		<Popup
			isOpen={isOpen}
			onClose={onClose}
			placement="bottom-start"
			content={getContent}
			zIndex={layers.modal + 1}
			trigger={(triggerProps) => (
				<Button
					{...triggerProps}
					appearance="link"
					spacing="none"
					onClick={toggleIsOpen}
					iconBefore={<InfoIcon size="small" label="info" primaryColor={ICON_COLOR} />}
					testId="project-create-importer-upload-flow.common.ui.info-button.button"
				/>
			)}
		/>
	);
};

const contentWrapperStyle = xcss({
	padding: 'space.300',
	maxWidth: '448px',
});
